import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ScanLine, BrainCircuit, RotateCcw, ArrowRight, ArrowLeft, Play, Check } from "lucide-react";
import { useLang, formatKickoff } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const R = 52;
const CIRC = 2 * Math.PI * R;

const DEFAULT_MATCHES = [];

const Crest = ({ label, color, crest }) =>
  crest ? (
    <img src={crest} alt={label} className="w-14 h-14 object-contain" />
  ) : (
    <div
      className="w-14 h-14 rounded-full flex items-center justify-center text-xs font-bold tracking-widest font-mono2"
      style={{ background: `${color}14`, border: `1px solid ${color}50`, color }}
    >
      {label}
    </div>
  );

export const DemoCard = () => {
  const { lang, t } = useLang();
  const [phase, setPhase] = useState("idle");
  const [typed, setTyped] = useState("");
  const [meta, setMeta] = useState(null);
  const [running, setRunning] = useState(false);
  const [matches, setMatches] = useState(DEFAULT_MATCHES);
  const [match, setMatch] = useState(null);
  const [analysesCount, setAnalysesCount] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/demo/stats`).then(({ data }) => setAnalysesCount(data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/demo/matches`)
      .then(({ data }) => {
        if (data.matches?.length) {
          setMatches(data.matches);
          setMatch((cur) => data.matches.find((m) => m.id === cur?.id) || data.matches[0]);
        }
      })
      .catch(() => {});
  }, []);

  const start = async () => {
    if (running || !match) return;
    setRunning(true);
    setPhase("scan");
    setTyped("");
    setMeta(null);
    setFailed(false);
    let gotDone = false;
    let gotMeta = false;
    try {
      const resp = await fetch(`${API}/demo/predict`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ match: match.id }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          const msg = JSON.parse(line.slice(5));
          if (msg.type === "meta") {
            gotMeta = true;
            setMeta(msg);
            setPhase("analyse");
          } else if (msg.type === "chunk") {
            setTyped((prev) => prev + msg.text);
          } else if (msg.type === "done") {
            gotDone = true;
            setPhase("verdict");
            setAnalysesCount((c) => (c ?? 55) + 1);
          } else if (msg.type === "error") {
            setFailed(true);
            setPhase("idle");
          }
        }
      }
      if (!gotDone && gotMeta) setPhase("verdict");
    } catch {
      setFailed(true);
      setPhase("idle");
    } finally {
      setRunning(false);
    }
  };

  const confiance = meta?.confiance ?? 0;

  const goWaitlist = () => {
    const el = document.getElementById("waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else window.location.href = "/#waitlist";
  };

  return (
    <div className="w-full max-w-3xl">
      <div className="neon-trace bg-[#0A0A0A] p-6 sm:p-10">
        <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-8">
          <span className="font-mono2 text-[10px] sm:text-xs tracking-[0.25em] text-[#CCFF00]">HITMAX // LIVE</span>
          <span className="flex items-center gap-2">
            <span className="font-mono2 text-[8px] tracking-widest uppercase text-zinc-500">{t.demoPage.realData}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#CCFF00] pulse-dot" />
          </span>
        </div>

        {phase !== "idle" && match && (
          <>
            <p className="font-mono2 text-[10px] tracking-widest text-zinc-500 uppercase mb-5">
              {match.league}
              {match.kickoff ? <span className="text-[#CCFF00]"> — {formatKickoff(match.kickoff, lang)}</span> : null}
            </p>
            <div className="flex items-center justify-between mb-10">
              <div className="flex flex-col items-center gap-2">
                <Crest label={match.homeCode} color="#CCFF00" crest={match.homeCrest} />
                <span className="font-display font-bold text-white">{match.home}</span>
              </div>
              <span className="font-mono2 text-sm text-zinc-600">VS</span>
              <div className="flex flex-col items-center gap-2">
                <Crest label={match.awayCode} color="#FFC800" crest={match.awayCrest} />
                <span className="font-display font-bold text-white">{match.away}</span>
              </div>
            </div>
          </>
        )}

        <div className="relative min-h-[280px] sm:min-h-[240px]">
          <AnimatePresence mode="wait">
            {phase === "idle" && (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="py-2">
                <p className="text-sm sm:text-base text-zinc-400 mb-6 text-center">{t.demoPage.idle}</p>
                {failed && (
                  <p data-testid="demo-unavailable-message" className="text-sm text-[#FFC800] mb-6 text-center">
                    {t.demoPage.unavailable}
                  </p>
                )}
                {matches.length === 0 && (
                  <p data-testid="demo-unavailable-message" className="text-sm text-[#FFC800] mb-6 text-center">
                    {t.demoPage.unavailable}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2.5 mb-8">
                  {matches.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`match-option-${m.id}`}
                      onClick={() => setMatch(m)}
                      className={`rounded-md border px-4 py-3.5 text-left transition-colors duration-300 ${
                        match?.id === m.id
                          ? "border-[#CCFF00]/60 bg-[#CCFF00]/5"
                          : "border-white/10 bg-[#111111] hover:border-white/25"
                      }`}
                    >
                      <p className="font-mono2 text-[8px] tracking-widest uppercase text-zinc-500 mb-1.5">{m.league}</p>
                      <p className={`font-display font-bold text-sm sm:text-base ${match?.id === m.id ? "text-[#CCFF00]" : "text-white"}`}>
                        {m.homeCode} <span className="text-zinc-600 font-normal">vs</span> {m.awayCode}
                      </p>
                    </button>
                  ))}
                </div>
                {matches.length > 0 && (
                <div className="flex justify-center">
                  <motion.button
                    data-testid="demo-start-button"
                    onClick={start}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center gap-2.5 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-sm rounded-sm px-8 py-4 hover:bg-[#b3e600] shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] transition-colors duration-300"
                  >
                    <Play className="w-4 h-4 fill-black" />
                    {t.demo.run}
                  </motion.button>
                </div>
                )}
              </motion.div>
            )}

            {phase === "scan" && (
              <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="space-y-3">
                <div className="flex items-center gap-2 text-[#CCFF00] mb-5">
                  <ScanLine className="w-4 h-4" />
                  <span className="font-mono2 text-[10px] tracking-widest uppercase">Analyse des données en cours…</span>
                </div>
                <div className="shimmer-bar h-3.5 rounded-sm bg-white/5" />
                <div className="shimmer-bar h-3.5 rounded-sm bg-white/5 w-4/5" />
                <div className="shimmer-bar h-3.5 rounded-sm bg-white/5 w-3/5" />
                <div className="shimmer-bar h-20 rounded-md bg-white/5 mt-6" />
                <motion.div
                  className="absolute left-0 right-0 h-16 bg-gradient-to-b from-transparent via-[#CCFF00]/25 to-transparent pointer-events-none"
                  animate={{ top: ["-15%", "105%"] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
                />
              </motion.div>
            )}

            {phase === "analyse" && (
              <motion.div key="analyse" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <div className="flex items-center gap-2 text-[#CCFF00] mb-5">
                  <BrainCircuit className="w-4 h-4" />
                  <span className="font-mono2 text-[10px] tracking-widest uppercase">L'IA rédige son analyse</span>
                </div>
                <p className="text-sm sm:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {typed}
                  <span className="type-caret text-[#CCFF00]">▌</span>
                </p>
              </motion.div>
            )}

            {phase === "verdict" && meta && (
              <motion.div
                key="verdict"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-[1fr_auto] gap-6 items-center"
              >
                <div>
                  <p className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Verdict</p>
                  <p data-testid="demo-verdict" className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight mb-4">{meta.verdict}</p>
                  <div className="flex gap-3">
                    <div className="rounded-md border border-white/10 bg-[#111111] px-4 py-3">
                      <p className="font-mono2 text-[9px] tracking-widest text-zinc-500 uppercase">Cote</p>
                      <p data-testid="demo-odds" className="font-display font-black text-2xl text-[#CCFF00] drop-shadow-[0_0_12px_rgba(204,255,0,0.4)]">{meta.cote}</p>
                    </div>
                    <div className="rounded-md border border-white/10 bg-[#111111] px-4 py-3">
                      <p className="font-mono2 text-[9px] tracking-widest text-zinc-500 uppercase">Probabilité</p>
                      <p data-testid="demo-probability" className="font-display font-black text-2xl text-white">{meta.probabilite}%</p>
                    </div>
                    {meta.score && (
                      <div className="rounded-md border border-white/10 bg-[#111111] px-4 py-3">
                        <p className="font-mono2 text-[9px] tracking-widest text-zinc-500 uppercase">{t.demoPage.scoreProbable}</p>
                        <p data-testid="demo-score" className="font-display font-black text-2xl text-[#FFC800]">{meta.score}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <svg width="130" height="130" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                    <motion.circle
                      cx="60"
                      cy="60"
                      r={R}
                      fill="none"
                      stroke="#CCFF00"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={CIRC}
                      transform="rotate(-90 60 60)"
                      initial={{ strokeDashoffset: CIRC }}
                      animate={{ strokeDashoffset: CIRC * (1 - confiance / 100) }}
                      transition={{ duration: 1.6, ease: "easeOut", delay: 0.3 }}
                      style={{ filter: "drop-shadow(0 0 8px rgba(204,255,0,0.5))" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span data-testid="demo-confidence" className="font-display font-black text-2xl text-white">{confiance}%</span>
                    <span className="font-mono2 text-[8px] tracking-widest uppercase text-zinc-500">Confiance</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {phase === "verdict" && typed && (
            <div data-testid="demo-analysis-points" className="mt-6 pt-5 border-t border-white/5">
              <ul className="space-y-2">
                {typed.split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />{l}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        </div>

        <div className="w-full max-w-3xl mt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          {analysesCount != null && (
            <p data-testid="demo-analyses-count" className="font-mono2 text-[10px] tracking-widest uppercase text-zinc-500">
              <span className="text-[#CCFF00] font-bold">{analysesCount}</span> {t.demoPage.analysesToday}
            </p>
          )}
          {phase !== "idle" && (
            <button
              data-testid="demo-replay-button"
              onClick={() => setPhase("idle")}
              disabled={running}
              className="flex items-center gap-2 font-mono2 text-[10px] tracking-widest uppercase text-white border border-white/15 rounded-sm px-5 py-3 hover:border-[#CCFF00]/60 hover:text-[#CCFF00] transition-colors duration-300 disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {t.demoPage.replay}
            </button>
          )}
          <AnimatePresence>
            {phase === "verdict" && (
              <motion.button
                data-testid="demo-cta-button"
                onClick={goWaitlist}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-xs rounded-sm px-6 py-3 hover:bg-[#b3e600] shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] transition-colors duration-300"
              >
                {t.demoPage.cta}
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
    </div>
  );
}

export default function DemoExperience() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#050505] font-body text-white">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="demo-logo" className="flex items-center">
            <img src="/logo-hitmax.png" alt="HitMax" className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="demo-back-link"
            className="flex items-center gap-2 font-mono2 text-[10px] tracking-widest uppercase text-zinc-400 hover:text-[#CCFF00] transition-colors duration-300"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t.demoPage.back}
          </Link>
        </div>
      </header>
      <div className="flex flex-col items-center justify-center p-4 sm:p-8 min-h-[calc(100vh-64px)]">
        <DemoCard />
      </div>
    </div>
  );
}
